import { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardBody,
  CardTitle,
  CardText,
  Row,
  Col,
  Spinner
} from "reactstrap";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import toast from "react-hot-toast";

// Chart colors for pie charts
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Get token from localStorage
        const token = localStorage.getItem("token");
        const baseURL = process.env.REACT_APP_API_URL || "https://api.pegasuschauffeurdrive.com";
        
        // Fetch dashboard statistics
        const dashboardResponse = await axios.get(
          `${baseURL}/api/dashboard/statistics`,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            }
          }
        );
        setDashboardData(dashboardResponse.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Prepare data for daily chart
  const prepareDailyChartData = () => {
    if (!dashboardData?.dailyData?.data) return [];
    
    return dashboardData.dailyData.data.map(item => ({
      ...item,
      day: new Date(item.date).getDate() // Extract just the day for display
    }));
  };

  // Prepare pie chart data for booking status
  const prepareBookingStatusData = () => {
    if (!dashboardData?.statusData?.statuses) return [];
    
    const { statuses } = dashboardData.statusData;
    
    return [
      { name: "Pending", value: statuses.pending },
      { name: "Accepted", value: statuses.accepted },
      { name: "Rejected", value: statuses.rejected }
    ];
  };

  // Prepare pie chart data for service types
  const prepareServiceTypeData = () => {
    if (!dashboardData?.serviceTypeData?.serviceTypes) return [];
    
    return dashboardData.serviceTypeData.serviceTypes.map(item => ({
      name: item.serviceType,
      value: item.count
    }));
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  };

  return (
    <div>
      <div style={{ height: "52px" }}>
        <h1 className="fw-bold" style={{ color: "#fff" }}>
          Dashboard
        </h1>
      </div>
      <hr style={{ color: "#fff", borderTop: "3px solid" }} />

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <Spinner color="light" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="bg-dark text-white border border-warning">
                <CardBody>
                  <CardTitle tag="h5">Total Bookings</CardTitle>
                  <CardText className="display-4">{dashboardData?.totals?.bookings || 0}</CardText>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="bg-dark text-white border border-warning">
                <CardBody>
                  <CardTitle tag="h5">Total Quotations</CardTitle>
                  <CardText className="display-4">{dashboardData?.totals?.quotations || 0}</CardText>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Total Revenue Card */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="bg-dark text-white border border-success">
                <CardBody>
                  <CardTitle tag="h5">Total Revenue (Accepted Bookings)</CardTitle>
                  <CardText className="display-3">
                    {formatCurrency(dashboardData?.revenueData?.totalRevenue || 0)}
                  </CardText>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Monthly Stats Chart */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="bg-dark text-white border border-warning">
                <CardBody>
                  <CardTitle tag="h5">Monthly Statistics - {new Date().getFullYear()}</CardTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={dashboardData?.monthlyStats || []}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fill: '#fff' }} 
                      />
                      <YAxis tick={{ fill: '#fff' }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }} 
                      />
                      <Legend />
                      <Bar dataKey="bookings" name="Bookings" fill="#ffb400" />
                      <Bar dataKey="quotations" name="Quotations" fill="#9c7900" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Monthly Revenue Chart */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="bg-dark text-white border border-success">
                <CardBody>
                  <CardTitle tag="h5">Monthly Revenue - {new Date().getFullYear()}</CardTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={dashboardData?.revenueData?.monthlyRevenue || []}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fill: '#fff' }} 
                      />
                      <YAxis 
                        tick={{ fill: '#fff' }} 
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="revenue" 
                        name="Revenue" 
                        stroke="#4caf50" 
                        strokeWidth={2}
                        dot={{ stroke: '#4caf50', strokeWidth: 2, r: 4 }}
                        activeDot={{ stroke: '#4caf50', strokeWidth: 2, r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Booking Status and Service Type Charts */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="bg-dark text-white border border-warning">
                <CardBody>
                  <CardTitle tag="h5">Booking Status Distribution</CardTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={prepareBookingStatusData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {prepareBookingStatusData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="bg-dark text-white border border-warning">
                <CardBody>
                  <CardTitle tag="h5">Service Type Distribution</CardTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={prepareServiceTypeData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {prepareServiceTypeData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Daily Data for Current Month */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="bg-dark text-white border border-warning">
                <CardBody>
                  <CardTitle tag="h5">
                    Daily Statistics - {dashboardData?.dailyData?.month} {dashboardData?.dailyData?.year}
                  </CardTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={prepareDailyChartData()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                      <XAxis 
                        dataKey="day" 
                        tick={{ fill: '#fff' }}
                        label={{ value: 'Day of Month', position: 'insideBottom', offset: -5, fill: '#fff' }}
                      />
                      <YAxis tick={{ fill: '#fff' }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }}
                        formatter={(value, name) => [value, name]}
                        labelFormatter={(label) => `Day ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="bookings" name="Bookings" fill="#ffb400" />
                      <Bar dataKey="quotations" name="Quotations" fill="#9c7900" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Dashboard;
